import {AfterViewInit, Component, ElementRef, forwardRef, inject, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {AppMainComponent} from '../app.main.component';

/**
 * Layout de la plantilla CBET (Avalon). Hereda toda la lógica de negocio de AppMainComponent
 * (roles, sockets, aviso de deuda, catálogo local) y solo cambia el comportamiento visual.
 * Se registra como AppMainComponent para que los componentes compartidos que lo inyectan sigan funcionando.
 */
@Component({
    selector: 'app-cbet-main',
    templateUrl: './cbet-main.component.html',
    styleUrls: ['./cbet-main.component.scss'],
    providers: [{ provide: AppMainComponent, useExisting: forwardRef(() => CbetMainComponent) }],
    standalone: false
})
export class CbetMainComponent extends AppMainComponent implements AfterViewInit, OnDestroy {

    @ViewChild('menuContainer') menuContainer: ElementRef<HTMLElement>;

    // Modos de Avalon: 'horizontal' (menú arriba), 'static' (lateral fijo), 'overlay' o 'slim'
    layoutMode: 'horizontal' | 'static' | 'overlay' | 'slim' = 'horizontal';

    rotateMenuButton: boolean;

    overlayMenuActive: boolean;

    staticMenuDesktopInactive: boolean;

    staticMenuMobileActive: boolean;

    // Alto real del menú horizontal: si sus opciones ocupan más de una fila, el contenido se corre hacia abajo
    menuHeight = 40;

    private readonly zone = inject(NgZone);

    private menuResize: ResizeObserver;

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined' || !this.menuContainer) {
            return;
        }
        this.menuResize = new ResizeObserver(entries => {
            const height = Math.round(entries[0].contentRect.height);
            if (height && height !== this.menuHeight) {
                this.zone.run(() => this.menuHeight = height);
            }
        });
        this.menuResize.observe(this.menuContainer.nativeElement);
    }

    ngOnDestroy(): void {
        this.menuResize?.disconnect();
        super.ngOnDestroy();
    }

    get mainPaddingTop(): number {
        // 60px del topbar + menú + 15px de aire (Avalon usa 115px fijos para una sola fila)
        return this.isHorizontal() && this.isDesktop() ? 60 + this.menuHeight + 15 : null;
    }

    onLayoutClick() {
        if (!this.topbarItemClick) {
            this.activeTopbarItem = null;
            this.topbarMenuActive = false;
        }

        if (!this.menuClick) {
            if (this.isHorizontal() || this.isSlim()) {
                this.menuService.reset();
            }

            if (this.overlayMenuActive || this.staticMenuMobileActive) {
                this.hideOverlayMenu();
            }

            this.menuHoverActive = false;
        }

        this.topbarItemClick = false;
        this.menuClick = false;
    }

    onMenuButtonClick(event) {
        this.menuClick = true;
        this.rotateMenuButton = !this.rotateMenuButton;
        this.topbarMenuActive = false;

        if (this.isOverlay()) {
            this.overlayMenuActive = !this.overlayMenuActive;
        } else if (this.isDesktop()) {
            this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
        } else {
            this.staticMenuMobileActive = !this.staticMenuMobileActive;
        }

        event.preventDefault();
    }

    onMenuClick(event) {
        this.menuClick = true;
    }

    onTopbarMenuButtonClick(event) {
        this.topbarItemClick = true;
        this.topbarMenuActive = !this.topbarMenuActive;
        this.hideOverlayMenu();
        event.preventDefault();
    }

    hideOverlayMenu() {
        this.rotateMenuButton = false;
        this.overlayMenuActive = false;
        this.staticMenuMobileActive = false;
    }

    isDesktop() {
        return typeof window === 'undefined' || window.innerWidth > 1024;
    }

    isMobile() {
        return typeof window !== 'undefined' && window.innerWidth <= 1024;
    }

    isHorizontal() {
        return this.layoutMode === 'horizontal';
    }

    isSlim() {
        return this.layoutMode === 'slim';
    }

    isOverlay() {
        return this.layoutMode === 'overlay';
    }
}
